import { t } from "i18next";
import dateFormat from "dateformat";
import { User } from "@onmo/graphql/graphql";

const convertTimestampToFormattedDate = (
  timestamp: string | number,
  isFormatDateFromUI?: boolean,
): string => {
  const formatDate = (date: Date): string => {
    const year = date.getUTCFullYear();
    const month = (date.getUTCMonth() + 1).toString().padStart(2, "0"); // Months are zero-based
    const day = date.getUTCDate().toString().padStart(2, "0");
    return isFormatDateFromUI
      ? `${day}/${month}/${year}`
      : `${year}-${month}-${day}`;
  };

  const date = new Date(timestamp);
  return formatDate(date);
};

const getTimeLeft = (endtime: Date | number | string) => {
  const timeNow = new Date();
  const targetDate = new Date(endtime);
  return targetDate.getTime() - timeNow.getTime();
};

const countDown = (endtime?: Date | number | string) => {
  if (!endtime) return "";
  const timeLeft = getTimeLeft(endtime);
  if (timeLeft < 0) {
    return t("common_Ended");
  }

  let seconds: string | number = Math.floor((timeLeft / 1000) % 60);
  let minutes: string | number = Math.floor((timeLeft / 1000 / 60) % 60);
  let hours: string | number = Math.floor((timeLeft / (1000 * 60 * 60)) % 24);
  const days: string | number = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
  const daysLeft = days + " " + t("time_days");

  if (hours < 10) hours = "0" + hours;
  if (minutes < 10) minutes = "0" + minutes;
  if (seconds < 10) seconds = "0" + seconds;

  if (days > 0) {
    return t("common_Ends in") + ": " + daysLeft;
  } else {
    return t("common_Ends in") + ": " + hours + ":" + minutes + ":" + seconds;
  }
};

const getTimeAgo = (publishTime: Date | number | string) => {
  const now = new Date();
  const publishDate = new Date(publishTime);
  const diffMs = now.getTime() - publishDate.getTime();

  // Convert to different time units
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffSeconds < 60) return `${diffSeconds} seconds ago`;
  if (diffMinutes < 60)
    return `${diffMinutes} minute${diffMinutes !== 1 ? "s" : ""} ago`;
  if (diffHours < 24)
    return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
  if (diffWeeks < 5)
    return `${diffWeeks} week${diffWeeks !== 1 ? "s" : ""} ago`;
  if (diffMonths < 12)
    return `${diffMonths} month${diffMonths !== 1 ? "s" : ""} ago`;
  return `${diffYears} year${diffYears !== 1 ? "s" : ""} ago`;
};

const getOnlineStatus = (endtime?: Date | number | string) => {
  if (!endtime) return "offline";

  const timeLeft = -getTimeLeft(endtime);
  const minutes: string | number = Math.floor(timeLeft / (1000 * 60));

  if (minutes < 10) {
    return "online";
  } else if (minutes < 60) {
    return "away";
  } else {
    return "offline";
  }
};

const getEndTime = (t: Function, endtime?: Date | number | string) => {
  if (!endtime) return "";

  const timeLeft = getTimeLeft(endtime);

  const hours: string | number = Math.floor((timeLeft / (1000 * 60 * 60)) % 24);
  const days: string | number = Math.floor(timeLeft / (1000 * 60 * 60 * 24));

  if (days < 1) {
    if (hours < 0) {
      return "";
    }
    return t("time_hours to go", { hours: hours });
  } else if (days > 30) {
    const endTimeMonth = new Date(endtime).getMonth();
    const nowMonth = new Date(Date.now()).getMonth();
    if (endTimeMonth > nowMonth)
      return t("time_months to go", { months: endTimeMonth - nowMonth });
    //over a year
    else
      return t("time_months to go", { months: endTimeMonth + 12 - nowMonth });
  } else {
    return t("time_days to go", { days: days });
  }
};

const getTimeDuration = (
  startTime: Date | number | string,
  endtime = new Date(),
) => {
  const startDate = new Date(startTime);
  return Math.floor((endtime.getTime() - startDate.getTime()) / 1000);
};

export const formatDate = (
  date: Date | string | number,
  formatType: string,
) => {
  const fDate = new Date(date);
  const diffDays = new Date().getDate() - fDate.getDate();
  const diffMonths = new Date().getMonth() - fDate.getMonth();
  const diffYears = new Date().getFullYear() - fDate.getFullYear();

  if (diffDays === 0 && diffMonths === 0 && diffYears === 0) {
    return t("today");
  }
  return dateFormat(date, formatType);
};

const getFormatDateLanguage = (user?: User): string => {
  if (!user?.language || user?.language.includes("en")) {
    return "en-GB";
  }
  return user?.language ?? "";
};

const renderDateTitle = (date: string, me?: User) => {
  if (date === "Today") return date;
  if (me) {
    return new Date(date).toLocaleDateString(getFormatDateLanguage(me), {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    });
  }
};

export const Time = {
  convertTimestampToFormattedDate,
  countDown,
  getOnlineStatus,
  getEndTime,
  getTimeAgo,
  getTimeDuration,
  formatDate,
  renderDateTitle,
};
